import { ReactElement, useEffect, useRef } from 'react';

import { useLatestFunc } from './hooks';
import { createCellEvent, getCell, getCellClassname, getCellStyle, onEditorNavigation } from './utils';
import type { CellKeyboardEvent, CellRendererProps, EditCellKeyDownArgs, Omit, RenderEditCellProps } from './types';
import {
  cellEditing,
  cellEditingInline,
  editableClassname,
  emptyCellClassname,
  invalidClassname,
  requiredClassname,
} from './style/cell.css';

/*
 * To check for outside `mousedown` events, we listen to all `mousedown` events at their birth,
 * i.e. on the window during the capture phase, and at their death, i.e. on the window during the bubble phase.
 *
 * We schedule a check at the birth of the event, cancel the check when the event reaches the "inside" container,
 * and trigger the "outside" callback when the event bubbles back up to the window.
 *
 * The event can be `stopPropagation()`ed halfway through, so they may not always bubble back up to the window,
 * so an alternative check must be used. The check must happen after the event can reach the "inside" container,
 * and not before it run to completion. `requestAnimationFrame` is the best way we know how to achieve this.
 * Usually we want click event handlers from parent components to access the latest commited values,
 * so `mousedown` is used instead of `click`.
 *
 * We must also rely on React's event capturing/bubbling to handle elements rendered in a portal.
 */

type SharedCellRendererProps<R, SR> = Pick<CellRendererProps<R, SR>, 'colSpan'>;

interface EditCellProps<R, SR>
  extends Omit<RenderEditCellProps<R, SR>, 'onRowChange' | 'onClose' | 'cellState'>,
    SharedCellRendererProps<R, SR> {
  rowIdx: number;
  onRowChange: (row: R, commitChanges: boolean, shouldFocusCell: boolean, shouldKeepOpen: boolean) => void;
  closeEditor: (shouldFocusCell: boolean) => void;
  navigate: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyDown?: (args: EditCellKeyDownArgs<R, SR>, event: CellKeyboardEvent) => void;
}

export default function EditCell<R, SR>({
  column,
  colSpan,
  row,
  rowIdx,
  onRowChange,
  closeEditor,
  onKeyDown,
  navigate,
}: EditCellProps<R, SR>): ReactElement {
  const frameRequestRef = useRef<number | undefined>();

  const { renderCell, cellClass, state, displayCellContent, renderEditCell } = getCell(column, row);

  const commitOnOutsideClick = !displayCellContent;

  // We need to prevent the `useEffect` from cleaning up between re-renders,
  // as `onWindowCaptureMouseDown` might otherwise miss valid mousedown events.
  // To that end we instead access the latest props via useLatestFunc.
  const commitOnOutsideMouseDown = useLatestFunc(() => {
    onClose(true, false);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: it's fine
  useEffect(() => {
    if (!commitOnOutsideClick) return;

    function onWindowCaptureMouseDown(): void {
      frameRequestRef.current = requestAnimationFrame(commitOnOutsideMouseDown);
    }

    addEventListener('mousedown', onWindowCaptureMouseDown, { capture: true });

    return () => {
      removeEventListener('mousedown', onWindowCaptureMouseDown, { capture: true });
      cancelFrameRequest();
    };
  }, [commitOnOutsideClick, commitOnOutsideMouseDown]);

  function cancelFrameRequest(): void {
    cancelAnimationFrame(frameRequestRef.current!);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    if (onKeyDown) {
      const cellEvent = createCellEvent(event);
      onKeyDown(
        {
          mode: 'EDIT',
          row,
          column,
          rowIdx,
          navigate() {
            navigate(event);
          },
          onClose,
        },
        cellEvent,
      );
      if (cellEvent.isGridDefaultPrevented()) return;
    }

    if (event.key === 'Escape') {
      // Discard changes
      onClose();
    } else if (event.key === 'Enter') {
      onClose(true);
    } else if (onEditorNavigation(event)) {
      navigate(event);
    }
  }

  function onClose(commitChanges = false, shouldFocusCell = true): void {
    if (commitChanges) {
      onRowChange(row, true, shouldFocusCell, false);
    } else {
      closeEditor(shouldFocusCell);
    }
  }

  function onEditorRowChange(row: R, commitChangesAndFocus = false, keepOpen = false): void {
    onRowChange(row, commitChangesAndFocus, commitChangesAndFocus, keepOpen);
  }

  const className = getCellClassname(
    column,
    'rdg-editor-container',
    editableClassname,
    cellEditing,
    !displayCellContent && cellEditingInline,
    {
      [requiredClassname]: state.required,
      [emptyCellClassname]: state.empty,
      [invalidClassname]: !state.valid,
    },
    cellClass,
  );

  return (
    <div
      role="gridcell"
      aria-colindex={column.idx + 1} // aria-colindex is 1-based
      aria-colspan={colSpan}
      aria-selected
      className={className}
      style={getCellStyle(column, colSpan)}
      onKeyDown={handleKeyDown}
      onMouseDownCapture={cancelFrameRequest}
      data-testid={column.key}
    >
      {renderEditCell != null && (
        <>
          {renderEditCell({
            column,
            row,
            cellState: state,
            onRowChange: onEditorRowChange,
            onClose,
          })}
          {displayCellContent &&
            renderCell?.({
              column,
              row,
              rowIdx,
              isCellEditable: true,
              cellState: state,
              tabIndex: -1,
              onRowChange: onEditorRowChange,
            })}
        </>
      )}
    </div>
  );
}
